import { normalizeContactCaptureSettings } from '../../../src/modules/contactCapture/helpers/contactCaptureSettings';
import type { PublicConfig } from './types';
import { WidgetAccessDeniedError } from './errors';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Only the envelope is validated here: a wrong shape, an unknown schema version or a missing id
 * means the response is not a widget config at all, and the caller renders a load error. The
 * settings themselves go through the platform normalizer, which clamps and defaults every value,
 * so both sides of the product agree on what a valid configuration is.
 */
export const normalizePublicConfig = (value: unknown): PublicConfig => {
  if (!isObject(value)) throw new Error('Invalid widget config: expected an object.');
  if (value.schemaVersion !== undefined && value.schemaVersion !== 1) {
    throw new Error('Unsupported widget config version.');
  }

  // The API calls the inline type "form".
  const type = value.type === 'form' ? 'inline' : value.type;
  if (type !== 'popup' && type !== 'inline') throw new Error('Invalid widget config type.');

  const id = value.id ?? value.publicId;
  if (typeof id !== 'string' || !id.trim()) {
    throw new Error('Invalid widget config: id is required.');
  }

  return {
    schemaVersion: 1,
    id,
    type,
    settings: normalizeContactCaptureSettings(
      isObject(value.settings) ? value.settings : null,
    ),
    consentText: typeof value.consentText === 'string' ? value.consentText : '',
  };
};

export const loadPublicConfig = async (url: string, expectedId: string): Promise<PublicConfig> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(url, { credentials: 'omit', signal: controller.signal });
    if (response.status === 403) throw new WidgetAccessDeniedError();
    if (!response.ok) throw new Error(`Widget config request failed with ${response.status}.`);
    const config = normalizePublicConfig(await response.json());
    if (config.id !== expectedId) throw new Error('Widget config does not match the requested capture.');
    return config;
  } finally {
    window.clearTimeout(timeout);
  }
};
